import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import { isConflictError } from '@/common/helpers/errorUtilities'

const DEFAULT_SNACKBAR_OPTIONS = { variant: 'error', autoHideDuration: 5000 }

/**
 * Hook that returns a handler for surfacing mutation errors to the user.
 *
 * On an HTTP 409 Conflict (a stale row version — the record was changed by
 * another process) it shows a "refresh the page and try again" message instead
 * of the caller's generic fallback. Any other error shows the fallback as-is.
 * Centralising the conflict copy here keeps it consistent across every page.
 *
 * @returns {(error: { status?: number | string }, fallbackMessage: string, options?: object) => void}
 *   Call inside a mutation `catch` block. `fallbackMessage` should already be
 *   translated by the caller. `options` are merged over the default
 *   `{ variant: 'error', autoHideDuration: 5000 }` notistack options.
 */
export const useMutationErrorSnackbar = () => {
    const { enqueueSnackbar } = useSnackbar()
    const { t } = useTranslation()

    return useCallback(
        (error, fallbackMessage, options = {}) => {
            const message = isConflictError(error)
                ? t('This was updated by someone else. Please refresh the page and try again.')
                : fallbackMessage

            enqueueSnackbar(message, { ...DEFAULT_SNACKBAR_OPTIONS, ...options })
        },
        [enqueueSnackbar, t]
    )
}
